/**
 * Threads API 미디어를 즉시 다운로드하여 로컬 Blob URL로 변환
 * @param {Object} thread - 미디어 정보가 포함된 스레드 객체
 * @returns {Promise<Object>} - Blob URL이 포함된 업데이트된 스레드 객체
 */
export const processThreadMediaDownload = async (thread) => {
  if (!thread.hasMedia || !thread.mediaUrls || thread.mediaUrls.length === 0) {
    return thread;
  }

  try {
    console.log(`🔄 Processing media for thread ${thread.id}`);
    
    // 모든 미디어 URL을 병렬로 다운로드
    const downloadedMediaUrls = await Promise.all(
      thread.mediaUrls.map(async (media) => {
        try {
          console.log(`📥 Downloading media: ${media.url}`);
          
          // axios 또는 fetch로 미디어 다운로드 (responseType: 'blob' 사용)
          const response = await fetch(media.url, {
            method: 'GET',
            mode: 'cors', // CORS 모드 명시
            credentials: 'omit' // 쿠키 제외
          });
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          // Blob으로 변환
          const blob = await response.blob();
          
          // Blob URL 생성
          const blobUrl = URL.createObjectURL(blob);
          
          console.log(`✅ Media downloaded and converted to blob URL`);
          
          return {
            ...media,
            originalUrl: media.url,
            url: blobUrl,
            isBlob: true,
            blobSize: blob.size,
            blobType: blob.type
          };
        } catch (error) {
          console.error(`❌ Failed to download media: ${error.message}`);
          
          // CORS 에러인 경우 원본 URL 사용 (Instagram CDN은 img 태그에서는 작동)
          if (error.message.includes('CORS') || error.message.includes('Failed to fetch')) {
            console.warn(`⚠️ CORS blocked, using original URL for display: ${media.url}`);
            return {
              ...media,
              url: media.url, // 원본 URL 유지
              isBlob: false,
              corsBlocked: true,
              warning: 'CORS 차단으로 인해 원본 URL 사용 중'
            };
          }
          
          // 실패한 경우 원본 URL 유지
          return {
            ...media,
            downloadError: error.message
          };
        }
      })
    );
    
    // 스레드 객체 업데이트
    return {
      ...thread,
      mediaUrls: downloadedMediaUrls,
      mediaProcessed: true
    };
    
  } catch (error) {
    console.error(`❌ Error processing media for thread ${thread.id}:`, error);
    return {
      ...thread,
      mediaProcessError: error.message
    };
  }
};

/**
 * Firebase Functions 프록시를 통한 미디어 URL 생성
 * @param {Object} media - 미디어 객체
 * @returns {Object} - 프록시 URL이 포함된 미디어 객체
 */
/**
 * Firebase Functions 프록시를 통한 미디어 URL 생성
 * @param {Object} media - 미디어 객체
 * @returns {Object} - 프록시 URL이 포함된 미디어 객체
 */
export const getProxiedMediaUrl = (media) => {
  // Firebase Functions URL
  const functionsUrl = 'https://us-central1-my-threads-insight.cloudfunctions.net';
  const proxiedUrl = `${functionsUrl}/proxyImage?url=${encodeURIComponent(media.url)}`;
  
  return {
    ...media,
    originalUrl: media.url,
    url: proxiedUrl,
    isProxied: true,
    requiresProxy: true
  };
};

/**
 * 미디어 처리: 프록시 URL 사용
 * @param {Object} thread - 스레드 객체
 * @returns {Object} - 프록시 URL이 적용된 스레드 객체
 */
export const processThreadMediaWithProxy = (thread) => {
  if (!thread.hasMedia || !thread.mediaUrls || thread.mediaUrls.length === 0) {
    return thread;
  }

  const proxiedMediaUrls = thread.mediaUrls.map(media => getProxiedMediaUrl(media));
  
  return {
    ...thread,
    mediaUrls: proxiedMediaUrls,
    mediaProcessed: true,
    usingProxy: true
  };
};

/**
 * 미디어 URL 메모리 정리
 * 컴포넌트 언마운트 시 호출하여 메모리 누수 방지
 * @param {Array} mediaUrls - 정리할 미디어 URL 배열
 */
export const cleanupMediaUrls = (mediaUrls) => {
  if (!mediaUrls || !Array.isArray(mediaUrls)) return;
  
  mediaUrls.forEach(media => {
    if (media.isBlob && media.url && media.url.startsWith('blob:')) {
      URL.revokeObjectURL(media.url);
      console.log(`🧹 Cleaned up blob URL: ${media.url}`);
    }
  });
};

/**
 * 미디어 다운로드 재시도 로직
 * URL 만료 시 새로운 URL 가져와서 재시도
 * @param {Object} thread - 스레드 객체
 * @param {Function} refreshThreadData - 스레드 데이터 새로고침 함수
 * @returns {Promise<Object>} - 업데이트된 스레드 객체
 */
export const retryMediaDownload = async (thread, refreshThreadData) => {
  try {
    console.log(`🔄 Retrying media download for thread ${thread.id}`);
    
    // 스레드 데이터 새로고침 (새 media_url 가져오기)
    const refreshedThread = await refreshThreadData(thread.id);
    
    if (!refreshedThread) {
      throw new Error('Failed to refresh thread data');
    }
    
    // 새로운 URL로 다시 다운로드 시도
    return await processThreadMediaDownload(refreshedThread);
  } catch (error) {
    console.error(`❌ Retry failed for thread ${thread.id}:`, error);
    return thread;
  }
};